// <i80-calendar>: our own component (not in Helios), made for zPlan. First version:
// a month grid and a week agenda, built only from i80 tokens (calendar.css).
import { I80Element, define, h } from "../base.js";
import css from "./calendar.css";

const COLORS = ["neutral", "action", "success", "warning", "critical"];

/** "2026-09-19" or "2026-09-19T09:00" → local Date at midnight (no time-zone shift). */
function parse(s) {
  const m = /^(\d{4})-(\d{2})(?:-(\d{2}))?/.exec(s ?? "");
  return m ? new Date(+m[1], +m[2] - 1, m[3] ? +m[3] : 1) : null;
}

/** The "09:00" part of a time or date-time attribute, or null for an all-day event. */
function parseTime(...values) {
  for (const v of values) {
    const m = /(?:^|T)([0-2]?\d):([0-5]\d)/.exec(v ?? "");
    if (m) return { h: +m[1], m: +m[2], key: +m[1] * 60 + +m[2] };
  }
  return null;
}

function formatTime(t, locale) {
  return t ? new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" }).format(new Date(2000, 0, 1, t.h, t.m)) : "";
}

function iso(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function startOfWeek(d, weekStart) {
  return addDays(d, -((d.getDay() - weekStart + 7) % 7));
}

function ensureStyles() {
  if (document.querySelector("style[data-i80-calendar]")) return;
  const style = document.createElement("style");
  style.setAttribute("data-i80-calendar", "");
  style.textContent = css;
  document.head.append(style);
}

/**
 * <i80-calendar month="2026-09" selected="2026-09-19">
 *   <i80-event date="2026-09-21" color="success" href="/plans/3">Launch</i80-event>
 *   <i80-event start="2026-09-22" end="2026-09-24">Offsite</i80-event>
 * </i80-calendar>
 *
 * Events are plain child elements, so a server (or an HTMX swap) can render them; the
 * calendar redraws when they change. Arrow keys move between days, Page Up/Down between
 * months, Enter selects. Fires i80-select ({date}) and i80-navigate ({month, view}).
 */
export class I80Calendar extends I80Element {
  static get observedAttributes() {
    return ["month", "selected", "view", "week-start", "locale", "today", "heading-level", "label-previous", "label-today", "label-next", "label-all-day"];
  }

  setup() {
    ensureStyles();
    this.querySelector(':scope > [data-i80-part="calendar"]')?.remove();
    this._root = h("div", { class: "i80-calendar", "data-i80-part": "calendar" });
    this.append(this._root);
    this._root.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-i80-date]");
      if (btn) this.select(btn.getAttribute("data-i80-date"));
      const nav = e.target.closest("[data-i80-nav]");
      if (nav) this.go(nav.getAttribute("data-i80-nav"));
    });
    this._root.addEventListener("keydown", (e) => this._key(e));
    // Redraw when the author's <i80-event> children change (HTMX swaps, JS), not for our own DOM.
    this._observer = new MutationObserver((records) => {
      const relevant = (r) => {
        if (r.target === this) return r.type === "childList" && [...r.addedNodes, ...r.removedNodes].some((n) => n !== this._root);
        return !this._root.contains(r.target);
      };
      if (records.some(relevant)) this.update();
    });
  }

  attributeChangedCallback(name, old, value) {
    // month/selected set from outside (not by our own navigation) re-anchor the view.
    if (!this._internal && (name === "month" || name === "selected")) this._focus = null;
    super.attributeChangedCallback(name, old, value);
  }

  _set(name, value) {
    this._internal = true;
    try {
      this.setAttribute(name, value);
    } finally {
      this._internal = false;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._observer?.disconnect();
  }

  get view() {
    return this.choice("view", ["month", "week", "day"], "month");
  }

  get weekStart() {
    const n = parseInt(this.getAttribute("week-start") ?? "1", 10);
    return n >= 0 && n <= 6 ? n : 1;
  }

  get locale() {
    return this.getAttribute("locale") || this.closest("[lang]")?.getAttribute("lang") || undefined;
  }

  get today() {
    return parse(this.getAttribute("today")) ?? parse(iso(new Date()));
  }

  /** The day the view is anchored on: focus, else the month attribute, else selected, else today. */
  get anchor() {
    return this._focus ?? parse(this.getAttribute("month")) ?? parse(this.getAttribute("selected")) ?? this.today;
  }

  get events() {
    return [...this.querySelectorAll(":scope > i80-event")].map((el) => {
      const from = el.getAttribute("date") ?? el.getAttribute("start");
      const start = parse(from);
      const end = parse(el.getAttribute("end")) ?? start;
      return {
        el, start, end,
        time: parseTime(el.getAttribute("time"), from),
        endTime: parseTime(el.getAttribute("end-time"), el.getAttribute("end")),
        color: COLORS.includes(el.getAttribute("color")) ? el.getAttribute("color") : "neutral",
        href: el.getAttribute("href"), text: el.textContent.trim(),
      };
    }).filter((e) => e.start);
  }

  /** Events on that day, all-day ones first, then by start time. */
  eventsOn(day) {
    return this.events
      .filter((e) => e.start <= day && day <= e.end)
      .sort((a, b) => (a.time?.key ?? -1) - (b.time?.key ?? -1));
  }

  update() {
    // Our own DOM changes must not trigger another render through the MutationObserver.
    this._observer?.disconnect();
    try {
      this._render();
    } finally {
      if (this.isConnected) this._observer?.observe(this, { childList: true, subtree: true, attributes: true, characterData: true });
    }
  }

  _render() {
    const anchor = this.anchor;
    const locale = this.locale;
    const month = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const view = this.view;
    const agenda = view === "week" || view === "day";
    const week = view === "week";
    const first = view === "day" ? anchor : startOfWeek(week ? anchor : month, this.weekStart);
    const days = agenda ? (week ? 7 : 1) : Math.ceil(((month.getDay() - this.weekStart + 7) % 7 + new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate()) / 7) * 7;
    const selected = this.getAttribute("selected");
    const today = iso(this.today);
    const focusDay = iso(anchor);
    const hadFocus = this._root.contains(document.activeElement) && document.activeElement.hasAttribute("data-i80-date");

    const long = new Intl.DateTimeFormat(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    const title = week
      ? `${new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" }).format(first)} – ${new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric" }).format(addDays(first, 6))}`
      : view === "day" ? long.format(anchor)
      : new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(month);
    const allDay = this.getAttribute("label-all-day") ?? "All day";
    const level = Math.min(Math.max(parseInt(this.getAttribute("heading-level") ?? "2", 10) || 2, 1), 6);

    const dateButton = (d, label) => {
      const key = iso(d);
      return h("button", {
        type: "button", class: "i80-calendar__date", "data-i80-date": key,
        tabindex: key === focusDay ? "0" : "-1",
        "aria-label": `${long.format(d)}${this.eventsOn(d).length ? `, ${this.eventsOn(d).length} ${this.eventsOn(d).length === 1 ? "event" : "events"}` : ""}`,
        "aria-pressed": String(key === selected),
        "aria-current": key === today ? "date" : null,
      }, label);
    };
    const timeRange = (e) => [formatTime(e.time, locale), e.endTime && formatTime(e.endTime, locale)].filter(Boolean).join("–");
    const eventList = (d) => {
      const list = this.eventsOn(d);
      if (!list.length) return null;
      return h("ul", { class: "i80-calendar__events" }, list.map((e) => {
        const range = timeRange(e);
        return h("li", {},
          agenda && h("span", { class: "i80-calendar__event-time" }, range || allDay),
          h(e.href ? "a" : "span", { class: `i80-calendar__event i80-calendar__event--color-${e.color}`, href: e.href, title: range ? `${range} ${e.text}` : e.text },
            !agenda && e.time ? h("span", { class: "i80-calendar__event-time" }, formatTime(e.time, locale)) : null,
            e.text));
      }));
    };
    const dayClass = (d) => [
      "i80-calendar__day",
      !agenda && d.getMonth() !== month.getMonth() && "i80-calendar__day--outside",
      iso(d) === today && "i80-calendar__day--today",
      iso(d) === selected && "i80-calendar__day--selected",
    ];

    let body;
    if (agenda) {
      const fmt = new Intl.DateTimeFormat(locale, { weekday: "short", day: "numeric" });
      body = h("ul", { class: "i80-calendar__week", "aria-label": title },
        Array.from({ length: days }, (_, i) => {
          const d = addDays(first, i);
          return h("li", { class: dayClass(d) }, dateButton(d, fmt.format(d)), eventList(d) ?? h("span", { class: "i80-calendar__empty" }, "—"));
        }));
    } else {
      const wd = new Intl.DateTimeFormat(locale, { weekday: "short" });
      const wdLong = new Intl.DateTimeFormat(locale, { weekday: "long" });
      body = h("table", { class: "i80-calendar__grid", "aria-label": title },
        h("thead", {}, h("tr", {}, Array.from({ length: 7 }, (_, i) => {
          const d = addDays(first, i);
          return h("th", { class: "i80-calendar__weekday", scope: "col", abbr: wdLong.format(d) }, wd.format(d));
        }))),
        h("tbody", {}, Array.from({ length: days / 7 }, (_, r) =>
          h("tr", {}, Array.from({ length: 7 }, (_, c) => {
            const d = addDays(first, r * 7 + c);
            return h("td", { class: dayClass(d) }, dateButton(d, d.getDate()), eventList(d));
          })))));
    }

    const navButton = (dir, iconName, text) =>
      h("i80-button", { color: "secondary", size: "small", icon: iconName, "icon-only": iconName ? "" : null, "data-i80-nav": dir }, text);
    const unit = view;
    this._root.replaceChildren(
      h("div", { class: "i80-calendar__header" },
        h(`h${level}`, { class: "i80-calendar__title", "aria-live": "polite" }, title),
        h("div", { class: "i80-calendar__nav" },
          navButton("prev", "chevron-left", this.getAttribute("label-previous") ?? `Previous ${unit}`),
          navButton("today", null, this.getAttribute("label-today") ?? "Today"),
          navButton("next", "chevron-right", this.getAttribute("label-next") ?? `Next ${unit}`))),
      body,
    );
    if (hadFocus) this._root.querySelector(`[data-i80-date="${focusDay}"]`)?.focus();
  }

  select(date) {
    const d = parse(date);
    if (!d) return;
    this._focus = d;
    this._set("month", iso(d).slice(0, 7));
    this._set("selected", iso(d));
    this.dispatchEvent(new CustomEvent("i80-select", { bubbles: true, detail: { date: iso(d), events: this.eventsOn(d).map((e) => e.el) } }));
  }

  /** Move the view: "prev", "next" or "today". */
  go(dir) {
    const a = this.anchor;
    const step = { week: 7, day: 1 }[this.view];
    let d;
    if (dir === "today") d = this.today;
    else if (step) d = addDays(a, dir === "next" ? step : -step);
    else d = new Date(a.getFullYear(), a.getMonth() + (dir === "next" ? 1 : -1), 1);
    this._moveTo(d, false);
  }

  _moveTo(d, focus) {
    const before = this.anchor;
    this._focus = d;
    this._set("month", iso(d).slice(0, 7));
    this.update();
    if (focus) this._root.querySelector(`[data-i80-date="${iso(d)}"]`)?.focus();
    if (before.getMonth() !== d.getMonth() || before.getFullYear() !== d.getFullYear() || this.view === "week") {
      this.dispatchEvent(new CustomEvent("i80-navigate", { bubbles: true, detail: { month: iso(d).slice(0, 7), date: iso(d), view: this.view } }));
    }
  }

  _key(e) {
    const btn = e.target.closest?.("[data-i80-date]");
    if (!btn) return;
    const d = parse(btn.getAttribute("data-i80-date"));
    const agenda = this.view !== "month";
    const moves = {
      ArrowRight: 1, ArrowLeft: -1, ArrowDown: agenda ? 1 : 7, ArrowUp: agenda ? -1 : -7,
      Home: -((d.getDay() - this.weekStart + 7) % 7), End: 6 - ((d.getDay() - this.weekStart + 7) % 7),
    };
    let to = null;
    if (e.key in moves) to = addDays(d, moves[e.key]);
    else if (e.key === "PageDown" || e.key === "PageUp") {
      const n = e.key === "PageDown" ? 1 : -1;
      to = new Date(d.getFullYear(), d.getMonth() + n, Math.min(d.getDate(), new Date(d.getFullYear(), d.getMonth() + n + 1, 0).getDate()));
    }
    if (!to) return;
    e.preventDefault();
    this._moveTo(to, true);
  }
}

define("i80-calendar", I80Calendar);
